import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Card, Table } from '../ui';
import type { TableColumn } from '../ui';
import useMediaQuery, { PHONE_QUERY } from '../clients/useMediaQuery';

interface MonthlyData {
  month: number;
  year: number;
  revenue: number;
  expenses: number;
  profit: number;
  margin: number;
}

interface PnLRow extends Record<string, unknown> {
  id: string;
  month: string;
  revenue: number;
  expenses: number;
  net: number;
  margin: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const getMonthName = (month: number) =>
  new Date(2000, month - 1, 1).toLocaleDateString('en-US', { month: 'short' });

const columns: TableColumn<PnLRow>[] = [
  { key: 'month', label: 'Month', mono: true },
  { key: 'revenue', label: 'Revenue', align: 'right', mono: true, render: (v: number) => formatCurrency(v) },
  { key: 'expenses', label: 'Expenses', align: 'right', mono: true, render: (v: number) => formatCurrency(v) },
  {
    key: 'net',
    label: 'Net',
    align: 'right',
    mono: true,
    render: (v: number) => (
      <span style={{ color: v >= 0 ? 'var(--status-up)' : 'var(--status-down)' }}>{formatCurrency(v)}</span>
    ),
  },
  { key: 'margin', label: 'Margin', align: 'right', mono: true, render: (v: number) => `${v.toFixed(1)}%` },
];

export default function PnLTable() {
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const phone = useMediaQuery(PHONE_QUERY);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [expensesResult, transactionsResult] = await Promise.all([
        supabase
          .from('financial_expenses')
          .select('month, year, amount')
          .order('year', { ascending: false })
          .order('month', { ascending: false }),
        supabase
          .from('stripe_transactions')
          .select('created_at, amount')
          .gt('amount', 0),
      ]);

      if (expensesResult.error) throw expensesResult.error;
      if (transactionsResult.error) throw transactionsResult.error;

      const expensesByMonth = new Map<string, number>();
      expensesResult.data?.forEach((exp) => {
        const key = `${exp.year}-${exp.month}`;
        expensesByMonth.set(key, exp.amount);
      });

      const revenueByMonth = new Map<string, number>();
      transactionsResult.data?.forEach((txn) => {
        const date = new Date(txn.created_at);
        const key = `${date.getFullYear()}-${date.getMonth() + 1}`;
        const current = revenueByMonth.get(key) || 0;
        revenueByMonth.set(key, current + txn.amount / 100);
      });

      const allKeys = new Set([
        ...Array.from(expensesByMonth.keys()),
        ...Array.from(revenueByMonth.keys()),
      ]);

      const data: MonthlyData[] = Array.from(allKeys)
        .map((key) => {
          const [year, month] = key.split('-').map(Number);
          const revenue = revenueByMonth.get(key) || 0;
          const expenses = expensesByMonth.get(key) || 0;
          const profit = revenue - expenses;
          const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

          return { month, year, revenue, expenses, profit, margin };
        })
        .sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.month - a.month;
        });

      setMonthlyData(data);
    } catch (err) {
      console.error('Error fetching PnL data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div
          style={{
            padding: 'var(--space-8) 0',
            textAlign: 'center',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-faint)',
          }}
        >
          Loading...
        </div>
      </Card>
    );
  }

  if (monthlyData.length === 0) {
    return (
      <Card eyebrow="P&L" title="No data yet">
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          Add expenses and sync Stripe to see your P&L.
        </p>
      </Card>
    );
  }

  const rows: PnLRow[] = monthlyData.map((data) => ({
    id: `${data.year}-${data.month}`,
    month: `${getMonthName(data.month)} ${data.year}`,
    revenue: data.revenue,
    expenses: data.expenses,
    net: data.profit,
    margin: data.margin,
  }));

  // Five money columns: tighter cells on phones, and scroll inside the card if still too wide
  return <Table columns={columns} rows={rows} dense={phone} style={{ overflowX: 'auto' }} />;
}
